import React from 'react';
/**
 * Hook para carregamento de dados do job existente
 *
 * Responsabilidades:
 * - Carregar dados do job via API
 * - Popular formData com dados do job
 * - Carregar auditores e locais do job
 * - Loading/Error states
 */
import { useState, useEffect, useCallback } from 'react';
import jobService from '../services/jobService';
import estabelecimentoService from '../services/estabelecimentoService';
import contagemService from '../services/contagemService';
import { STATUS_MAP } from '../constants/status';
import type { User } from '../services/userService';
import type { LocalContagem } from '../types/estabelecimento';

export interface JobFormData {
  dataHora: string;
  status: string;
  envioSaidos: string;
  envioSaldosCalculado: string;
  cliente: string;
  observacoes: string;
  motivoCancelamento: string;
  auditorSelecionado: string;
  observacaoDetalhada: string;
  ordemColeta: string;
  lacres: string;
  horaInicio: string;
  horaTermino: string;
  envioRelatorio: string;
  locais: string;
  inicio: string;
  termino: string;
}

export interface UseContagemJobLoaderReturn {
  // Estados
  loading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;

  // Form Data
  formData: JobFormData;
  setFormData: React.Dispatch<React.SetStateAction<JobFormData>>;

  // Clientes do job
  selectedClienteIds: number[];
  setSelectedClienteIds: React.Dispatch<React.SetStateAction<number[]>>;
  jobClientes: any[];
  setJobClientes: React.Dispatch<React.SetStateAction<any[]>>;

  // Hospital
  selectedHospitalId: number;
  setSelectedHospitalId: React.Dispatch<React.SetStateAction<number>>;

  // Callbacks para popular auditores/locais após load
  loadJobAuditores: (job: any) => User[];
  loadJobLocais: (job: any, hospitalId: number) => Promise<LocalContagem[]>;
}

const createInitialFormData = (dateParam?: string | null): JobFormData => {
  const initialDate = dateParam
    ? dateParam.split('-').reverse().join('/')  // YYYY-MM-DD → DD/MM/YYYY
    : new Date().toLocaleDateString('pt-BR');

  return {
    dataHora: initialDate,
    // #8: default da NOVA contagem agora e 'Agendada' (era 'Pré-Agendada').
    // Continua editável no ato da criação.
    status: STATUS_MAP['AGENDADA'],
    envioSaidos: '',
    envioSaldosCalculado: '',
    cliente: '',
    observacoes: '',
    motivoCancelamento: '',
    auditorSelecionado: '',
    observacaoDetalhada: '',
    ordemColeta: '',
    lacres: '',
    horaInicio: '',
    horaTermino: '',
    envioRelatorio: '',
    locais: '',
    inicio: '07:00',
    termino: '09:00'
  };
};

export function useContagemJobLoader(
  id: string | undefined,
  isNewContagem: boolean,
  dateParam?: string | null
): UseContagemJobLoaderReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data state
  const [formData, setFormData] = useState<JobFormData>(() =>
    createInitialFormData(isNewContagem ? dateParam : null)
  );

  // Clientes state
  const [selectedClienteIds, setSelectedClienteIds] = useState<number[]>([]);
  const [jobClientes, setJobClientes] = useState<any[]>([]);

  // Hospital state
  const [selectedHospitalId, setSelectedHospitalId] = useState<number>(0);

  // Load auditores from job data
  const loadJobAuditores = useCallback((job: any): User[] => {
    if (job.auditores && job.auditores.length > 0) {
      return job.auditores.map((aud: any) => ({
        id: aud.id,
        username: aud.nome,
        email: aud.email,
        ativo: true,
        role: 'OPERATOR'
      }));
    }
    return [];
  }, []);

  // Load locais from job data
  const loadJobLocais = useCallback(async (job: any, hospitalId: number): Promise<LocalContagem[]> => {
    if (!job.locais_contagem || job.locais_contagem.length === 0) {
      return [];
    }

    try {
      const locaisDisponiveis = await estabelecimentoService.listLocaisContagem(hospitalId);
      return job.locais_contagem
        .map((loc: any) => locaisDisponiveis.find(ld => ld.id === loc.id))
        .filter((l: any) => l !== undefined);
    } catch (err) {
      console.error('Erro ao carregar locais selecionados:', err);
      return [];
    }
  }, []);

  // Load job data when component mounts or id changes
  useEffect(() => {
    const loadJobData = async () => {
      if (isNewContagem || !id) return;

      setLoading(true);
      try {
        const job = await jobService.getJob(parseInt(id));
        console.log('✅ Job carregado:', job);

        // Parse date without timezone issues
        const [year, month, day] = job.data_prevista.split('T')[0].split('-');
        const dataLocal = `${day}/${month}/${year}`;

        setFormData({
          dataHora: dataLocal,
          status: STATUS_MAP[job.status] || STATUS_MAP['PRE_AGENDADA'],
          envioSaidos: '',
          envioSaldosCalculado: job.envio_saldos_calculado || '',
          cliente: '',
          observacoes: job.descricao || '',
          motivoCancelamento: job.motivo_cancelamento || '',
          auditorSelecionado: '',
          observacaoDetalhada: '',
          ordemColeta: job.ordem_coleta || '',
          locais: '',
          lacres: job.lacre_qtd_coleta_manual || '',
          inicio: job.hora_inicio || '07:00',
          horaInicio: job.hora_inicio_contagem || '',
          termino: job.hora_fim || '09:00',
          horaTermino: job.hora_termino_contagem || '',
          envioRelatorio: job.data_envio_relatorio || ''
        });

        // Set clientes - ensure com_regime and sem_regime are included
        if (job.clientes && job.clientes.length > 0) {
          setSelectedClienteIds(job.clientes.map((c: any) => c.id));
          // Ensure each client has com_regime/sem_regime fields
          const clientesWithRegime = job.clientes.map((c: any) => ({
            ...c,
            com_regime: c.com_regime ?? false,
            sem_regime: c.sem_regime ?? false,
          }));
          setJobClientes(clientesWithRegime);
        } else if (job.cliente_id) {
          setSelectedClienteIds([job.cliente_id]);
          // Fallback for legacy data - assume no regime info available
          setJobClientes([{
            id: job.cliente_id,
            nome: job.cliente_nome_fantasia || job.cliente_razao_social,
            com_regime: false,
            sem_regime: false,
          }]);
        }

        // Set hospital
        setSelectedHospitalId(job.hospital_id);

      } catch (err: any) {
        console.error('❌ Erro ao carregar job:', err);
        setError(err.message || 'Erro ao carregar dados do job');
      } finally {
        setLoading(false);
      }
    };

    loadJobData();
  }, [id, isNewContagem]);

  return {
    loading,
    error,
    setError,
    formData,
    setFormData,
    selectedClienteIds,
    setSelectedClienteIds,
    jobClientes,
    setJobClientes,
    selectedHospitalId,
    setSelectedHospitalId,
    loadJobAuditores,
    loadJobLocais,
  };
}
